import React from 'react';
import { Grid, CardContent, TextField } from '@mui/material';
import { Formik } from 'formik';
import { UpdateUserPayload, useUpdateCurrentUserMutation } from 'src/redux/services/users';
import PhoneInput, { CountryData } from 'react-phone-input-2';
import countries from 'src/components/PhoneInput/countries';
import ButtonComponent from 'src/components/ButtonComponent';
import 'react-phone-input-2/lib/material.css';
import * as Yup from 'yup';
import { createInitialPrefix } from 'src/pages/Login/forms/SignupForm';
import { WhiteContainer, FormContainer, PhoneErrorWrapper, ContainerCard, TypographyGridTitle, ActionsWrapper } from 'src/style/common';
import SnackBarCompleteHandler from 'src/components/SnackBarCompleteHandler';
import { User } from 'src/dto/User';

export interface GeneralTabProps {
  user: User;
}

const GeneralTab: React.FC<GeneralTabProps> = ({ user }) => {
  const [updateUser, { isLoading: updateUserIsLoading, isError: updateUserIsError, isSuccess: updateUserIsSuccess, error: updateUserErrorMessage }] =
    useUpdateCurrentUserMutation();
  const [plusNeeded, setPlusNeeded] = React.useState<boolean>(false);

  const phoneCountryCodeToDigits =
    user?.phone_country_code && countries.find((el) => el.iso2 === user?.phone_country_code?.toLocaleLowerCase())
      ? `+${countries.find((el) => el.iso2 === user?.phone_country_code?.toLocaleLowerCase())?.value}`
      : user?.phone_country_code;
  const initialPrefix = phoneCountryCodeToDigits || createInitialPrefix()?.iso2 || 'us';
  const initialPhoneNumber = user?.phone ? user.phone.replace(`+${initialPrefix}`, '') : user?.phone;

  const DisplayingErrorMessagesSchema = Yup.object().shape({
    name: Yup.string().required('This field is required'),
    lastName: Yup.string().required('This field is required'),
    email: Yup.string().email('Email is not valid').required('This field is required'),
    phone_prefix: Yup.string(),
    phone: Yup.string().required('This field is required'),
  });

  return (
    <WhiteContainer container={true}>
      <Grid item={true} xs={12} md={3} lg={3} xl={3}>
        <ContainerCard>
          <TypographyGridTitle variant="h5">Personal Information</TypographyGridTitle>
        </ContainerCard>
      </Grid>

      <Grid item={true} xs={12} md={9} lg={9} xl={9}>
        <ContainerCard>
          <Formik
            initialValues={{
              name: user.first_name,
              lastName: user.last_name,
              phone: initialPhoneNumber,
              email: user.email,
              phone_prefix: initialPrefix,
            }}
            validateOnChange={true}
            validationSchema={DisplayingErrorMessagesSchema}
            onSubmit={async (values) => {
              const phoneCountryCodeDigits = countries.find((el) => el.iso2 === values.phone_prefix);
              const updateUserData: UpdateUserPayload = {
                first_name: values.name ? values.name : '',
                last_name: values.lastName ? values.lastName : '',
                phone_country_code: phoneCountryCodeDigits?.value ? `+${phoneCountryCodeDigits?.value}` : user.phone_country_code,
                phone: values.phone && values.phone.trim().length > 0 ? (plusNeeded ? `+${values.phone}` : `${values.phone}`) : '',
              };
              await updateUser(updateUserData);
            }}
          >
            {(formikProps) => {
              const {
                errors: { name: nameError, lastName: lastNameError },
                touched: { name: nameTouched, lastName: lastNameTouched, phone: phoneTouched },
              } = formikProps;

              return (
                <>
                  <CardContent style={{ padding: 0 }}>
                    <FormContainer
                      onSubmit={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        formikProps.handleSubmit();
                      }}
                    >
                      <TextField
                        variant="outlined"
                        label="First Name *"
                        data-testid="first-name-input"
                        error={nameTouched && !!nameError}
                        helperText={nameTouched && nameError}
                        {...formikProps.getFieldProps('name')}
                      />
                      <TextField
                        variant="outlined"
                        label="Last Name *"
                        error={lastNameTouched && !!lastNameError}
                        helperText={lastNameTouched && lastNameError}
                        {...formikProps.getFieldProps('lastName')}
                      />
                      <PhoneInput
                        dropdownStyle={{ position: 'fixed' }}
                        country={formikProps.getFieldProps('phone_prefix').value}
                        disabled={user.mfa_list && user.mfa_list.length > 0}
                        specialLabel="Phone *"
                        enableSearch
                        isValid={!formikProps.errors['phone']}
                        value={formikProps.getFieldProps('phone').value}
                        onChange={(phone, obj: CountryData) => {
                          formikProps.setFieldValue('phone_prefix', obj.countryCode, true);
                          formikProps.setFieldValue('phone', phone, true);
                          setPlusNeeded(true);
                        }}
                        onBlur={() => {
                          formikProps.setFieldTouched('phone_prefix');
                          formikProps.validateField('phone_prefix');
                          formikProps.setFieldTouched('phone');
                          formikProps.validateField('phone');
                        }}
                        inputStyle={{ width: '100%' }}
                      />
                      {phoneTouched && formikProps.errors['phone'] && <PhoneErrorWrapper>{formikProps.errors['phone']}</PhoneErrorWrapper>}
                      <TextField variant="outlined" label="Email Address" disabled={true} {...formikProps.getFieldProps('email')} />
                    </FormContainer>
                  </CardContent>

                  <ActionsWrapper hAlign="flex-end">
                    <ButtonComponent buttontype="cancel" onClick={() => formikProps.handleReset()} />
                    <ButtonComponent
                      buttontype="mainaction"
                      text="Save"
                      data-testid="save-button"
                      disabled={updateUserIsLoading || !(Object.keys(formikProps.errors).length === 0 && formikProps.dirty)}
                      onClick={() => formikProps.submitForm()}
                    />
                  </ActionsWrapper>
                </>
              );
            }}
          </Formik>
        </ContainerCard>
        <SnackBarCompleteHandler
          isSuccess={updateUserIsSuccess}
          isError={updateUserIsError}
          error={updateUserErrorMessage}
          successMessage="User successfully updated"
        />
      </Grid>
    </WhiteContainer>
  );
};

export default GeneralTab;
